<template>
  <div class="mb-4">
    <p class="text-center" style="font-size: 18px;">{{infoLabel}}</p>
  </div>
  <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
    <div class="row">
      <div class="col-lg-2 col-md-6 col-sm-12">
        <Datepicker
            v-model="formData.date"
            :required="true"
            :label="$t('general.date')"
            position="down"
            :validation="v$.date"
            :messages="v$.date.required.$message"/>
      </div>
      <div class="col-lg-4 col-md-6 col-sm-12">
        <BaseInput
            v-model="formData.startLocation"
            autocomplete="on"
            nameForAutocomplete="startLocation"
            :label="$t('general.start_location')"/>
      </div>
      <div class="col-lg-4 col-md-6 col-sm-12">
        <BaseInput
            v-model="formData.endLocation"
            autocomplete="on"
            nameForAutocomplete="endLocation"
            :label="$t('general.end_location')"/>
      </div>
      <div class="col-lg-2 col-md-4 col-sm-12">
        <BaseNumber
            v-model="formData.distance"
            :decimal-places=2
            :label="$t('general.kilometer')"/>
      </div>
      <div class="col-12">
        <BaseInput
            v-model="formData.reason"
            :label="$t('general.reason')"/>
      </div>
    </div>
    <div class="form-buttons-w text-center">
      <button class="btn btn-primary" type="submit">{{ $t('general.button.save') }}</button>
    </div>
  </form>
</template>

<script setup>
import travelExpensesService from '@/api/TravelExpenses';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseNumber from '@/components/base/BaseNumber.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {requiredRule} from '@/shared/validators/validationRules';
import useVuelidate from '@vuelidate/core';
import {computed, defineEmits, defineProps, onMounted, ref, toRef, toRefs} from 'vue';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();
const isSubmitting = ref(false);
const emit = defineEmits(['submit']);
const isLoaded = ref(false);

const props = defineProps({
  travelExpenses: {
    type: Object,
    required: false,
  },
  itemData: {
    type: Object,
    required: false,
  },
  infoLabel:{
    type: String,
    required: false,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const itemData = toRef(props, 'itemData');
const travelExpenses = toRef(props, 'travelExpenses')
const infoLabel = toRef(props, 'infoLabel')

const formData = ref({
  id: Number(itemData.value?.id ?? ''),
  travelExpenseId: travelExpenses.value?.id ?? null,
  date: itemData.value?.date ?? '',
  startLocation: itemData.value?.startLocation ?? '',
  endLocation: itemData.value?.endLocation ?? '',
  distance: itemData.value?.distance ?? 0,
  reason: itemData.value?.reason ?? '',
});

const rules = computed(() => {
  return {
    date: requiredRule(t),
  };
});

const v$ = useVuelidate(rules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (typeof formData.value.distance === 'string') {
      formData.value.distance = +formData.value.distance;
    }

    if (formData.value.id) {
      await travelExpensesService.putTravelExpenses(formData.value, +formData.value.id);
    } else {
      await travelExpensesService.postTravelExpenses(formData.value);
    }

    isSubmitting.value = false;

    emit('createTravelExpenses', travelExpenses.value?.id);
    emit('update:submitForm', !props.submitForm);
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(async () => {
  CloseToast();
  isLoaded.value = true;
});
</script>

<style scoped>
</style>